/**
 * useAdminCompanies Hook
 *
 * React Query hooks for admin companies.
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { useQuery } from '@tanstack/react-query';
import { adminService } from '../services/admin.service';

/**
 * Query key factory for admin companies
 */
export const adminCompanyKeys = {
  all: ['admin', 'companies'] as const,
  lists: () => [...adminCompanyKeys.all, 'list'] as const,
  list: () => [...adminCompanyKeys.lists()] as const,
  details: () => [...adminCompanyKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminCompanyKeys.details(), id] as const,
};

/**
 * Query key factory for admin brands
 */
export const adminBrandKeys = {
  all: ['admin', 'brands'] as const,
  details: () => [...adminBrandKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminBrandKeys.details(), id] as const,
  posConfig: (brandId: string) => [...adminBrandKeys.all, 'pos-config', brandId] as const,
  salesSyncStatus: (brandId: string) => [...adminBrandKeys.all, 'sales-sync-status', brandId] as const,
};

/**
 * Hook to fetch all companies list
 */
export function useAdminCompanies() {
  return useQuery({
    queryKey: adminCompanyKeys.list(),
    queryFn: () => adminService.listCompanies(),
  });
}

/**
 * Hook to fetch single company detail with brands tree
 */
export function useAdminCompany(id: string | null) {
  return useQuery({
    queryKey: adminCompanyKeys.detail(id!),
    queryFn: () => adminService.getCompany(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch single brand detail
 */
export function useAdminBrand(id: string | null) {
  return useQuery({
    queryKey: adminBrandKeys.detail(id!),
    queryFn: () => adminService.getBrand(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch POS config for a brand
 */
export function useAdminPosConfig(brandId: string | null) {
  return useQuery({
    queryKey: adminBrandKeys.posConfig(brandId!),
    queryFn: () => adminService.getPosConfig(brandId!),
    enabled: !!brandId,
  });
}

/**
 * Hook to fetch sales sync status for a brand
 */
export function useAdminSalesSyncStatus(brandId: string | null, enabled = true) {
  return useQuery({
    queryKey: adminBrandKeys.salesSyncStatus(brandId!),
    queryFn: () => adminService.getSalesSyncStatus(brandId!),
    enabled: !!brandId && enabled,
  });
}
